
import { useState, useCallback } from "react";
import Header from "@/components/Header";
import NavBar from "@/components/NavBar";
import FriendsList from "@/components/FriendsList";
import { useAuth } from "@/context/AuthContext";
import { useFriendships } from "@/hooks/useFriendships";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { createInvite } from "@/integrations/supabase/inviteService";
import { updateManualPlayer } from "@/integrations/supabase/manualPlayerService";

const Friends = () => {
  const { isLoading: isAuthLoading } = useAuth();
  const {
    friends,
    pendingRequests,
    sentRequests,
    isLoading,
    sendFriendRequest,
    acceptFriendRequest,
    rejectFriendRequest,
    removeFriend,
    removeManualFriend,
    refreshFriendships,
    isProcessing
  } = useFriendships();

  const [invitedPlayerIds, setInvitedPlayerIds] = useState<Set<string>>(new Set());
  const isRefreshing = isLoading || isProcessing;

  const handleRefresh = () => {
    refreshFriendships();
  };

  const handleInviteManualPlayer = useCallback(async (manualPlayerId: string, email: string) => {
    // Save email to manual player if it wasn't already set
    await updateManualPlayer(manualPlayerId, { email });

    const { error } = await createInvite(email, { manualPlayerId });
    if (error) {
      toast.error("Failed to send invite");
      return;
    }

    setInvitedPlayerIds(prev => new Set(prev).add(manualPlayerId));
    toast.success(`Invite sent to ${email}`);
  }, []);

  if (isAuthLoading) {
    return (
      <div className="min-h-screen pb-16">
        <Header title="Friends" />
        <div className="p-4 space-y-3">
          {Array(3).fill(0).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4 flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                <div className="flex-1">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-44 mt-1.5" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <NavBar />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16">
      <Header title="Friends">
        <Button
          variant="outline"
          size="icon"
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </Button>
      </Header>
      <FriendsList
        friends={friends}
        pendingRequests={pendingRequests}
        sentRequests={sentRequests}
        isLoading={isLoading}
        sendFriendRequest={sendFriendRequest}
        acceptFriendRequest={acceptFriendRequest}
        rejectFriendRequest={rejectFriendRequest}
        removeFriend={removeFriend}
        removeManualFriend={removeManualFriend}
        refreshFriendships={refreshFriendships}
        onInviteManualPlayer={handleInviteManualPlayer}
        invitedPlayerIds={invitedPlayerIds}
      />
      <NavBar />
    </div>
  );
};

export default Friends;
